import * as React from 'react';
import styles from './widget.module.scss';
import EligibleWidget from './eligible';
import WidgetHeader from './header';
import CircleProgress from '../../_common/progress/circle/circleProgress';
import BorrowerNotEligibleWidget from './notEligible/borrower';
import Frame from '../../_common/frame';
import CompanyNotEligibleWidget from './notEligible/company';
import SystemMaintenanceWidget from './systemMaintenance';
import { observer, inject } from 'mobx-react';
import { WidgetApi } from '../../../apis/widgetApi';
import { BorrowerApi } from '../../../apis/borrowerApi';
import { AdminApi } from '../../../apis/adminApi';
import { DialogStore } from '../../../stores/dialogStore';
import { WidgetStateEnum, FeatureFlagEnum } from '../../../models/dtos/enums';
import { LoanStore } from '../../../stores/loanStore';
import { RouteComponentProps } from 'react-router-dom';
import {
  WidgetDto,
  NonEligibleWidgetDto,
  OutstandingLoanWidgetDto,
  BenefitsWidgetDto,
  convertNumberToWidgetStateEnum,
  convertWidgetStateToGACategory,
} from '../../../models/dtos/widget';
import { dependencies, KYC_ROUTE } from '../../../models/constants';
import OutstandingLoanWidget from './outstandingLoan';
import LoanRequestedWidget from './loanRequested';
import { HubService } from '../../../services/hubService';
import { BorrowerStore } from '../../../stores/borrowerStore';
import { WidgetStore } from '../../../stores/widgetStore';
import { CompanyStore } from '../../../stores/companyStore';
import UnconsentedWidget from './unconsented';
import ConsentAgreement from './consentAgreement';
import { CompanyApi } from '../../../apis/companyApi';
import { Parent } from '../../../services/parentService';
import { DocumentsApi } from '../../../apis/documentsApi';
import UnauthorizedWidget from './unauthorized';
import { LoansApi } from '../../../apis/loansApi';
import { event } from '../../../utils/tracking';
import { FeatureFlagApi } from '../../../apis/featureFlagApi';
import { WidgetService } from '../../../utils/widgetService';
import { PageStepperStore } from '../../../stores/pageStepperStore';
import { KycApi } from '../../../apis/kycApi';
import { isKycFormRequired } from '../../../utils/kycGate';
import { onKycCompleted } from '../../../utils/kycCompletion';
import KycIntroWidget from './kycIntro';
import KycGateErrorWidget from './kycGateError';
export interface ChildWidgetProps {
  onFinished(): void;
  onError(error?: any): void;
  widgetStore: WidgetStore;
}

export interface WidgetProps extends RouteComponentProps<{}> {
  widgetApi: WidgetApi;
  borrowerApi: BorrowerApi;
  adminApi: AdminApi;
  loanStore: LoanStore;
  hubService: HubService;
  borrowerStore: BorrowerStore;
  widgetStore: WidgetStore;
  companyApi: CompanyApi;
  documentsApi: DocumentsApi;
  loansApi: LoansApi;
  featureFlagApi: FeatureFlagApi;
  companyStore: CompanyStore;
  pageStepperStore: PageStepperStore;
}

export interface WidgetState {
  widget: WidgetDto;
  errorStatus: string;
  isUnderCreditLine?: boolean;
  isCreditLine?: boolean;
  isBelowMinimumMLA?: boolean;
  // b.ii.2: KYC gate. The form itself is a page opened in the HR dashboard's
  // modal, so the widget only tracks whether the borrower still owes KYC.
  kycRequired?: boolean;
  kycGateError?: boolean;
}

@inject(...dependencies)
@observer
export class Widget extends React.Component<WidgetProps, WidgetState> {
  loadingStore = new DialogStore();
  featureFlagApi = new FeatureFlagApi();
  widgetService = new WidgetService();
  kycApi = new KycApi();
  private stopListeningForKyc?: () => void;

  constructor(props: WidgetProps) {
    super(props);

    this.state = {
      widget: {
        state: WidgetStateEnum.Error,
      } as WidgetDto,
      errorStatus: '',
    };

    // Reset loan ranges on widget initialization
    this.props.loanStore.resetLoanRanges();
    this.props.hubService.onUpdateWidget(this.onUpdateWidget.bind(this));
  }

  onUpdateWidget = async (borrowerId: number, state: number) => {
    const { borrowerStore } = this.props;
    const widgetState = convertNumberToWidgetStateEnum(state);
    if (borrowerStore.hasBorrower && borrowerStore.borrower.id === borrowerId) {
      // if backend returns outstanding loan widget,
      // reinitialize the widget so that we can get the response with
      // outstanding loan details
      if (widgetState === WidgetStateEnum.OutstandingLoan) {
        this.loadingStore.show();
        await this.initialize();
        this.loadingStore.hide();
      } else if (widgetState === WidgetStateEnum.LoanRequested) {
        this.setState({ widget: { state: widgetState } });
      }
    }
  };

  initializeBorrower = async () => {
    const { borrowerStore } = this.props;
    if (
      this.state.widget.state === WidgetStateEnum.BenefitsEmployee ||
      this.state.widget.state === WidgetStateEnum.BenefitsAdmin ||
      this.state.widget.state === WidgetStateEnum.Unconsented ||
      this.state.widget.state === 'Error'
    ) {
      return;
    } else {
      await borrowerStore.setBorrowerStoreValue();
    }
  };

  initialize = async () => {
    try {
      const widget = await this.props.widgetApi.getWidget();

      const [isCreditLineEnabled] = await Promise.all([
        this.widgetService
          .fetchFeatureFlag(
            this.props.featureFlagApi,
            FeatureFlagEnum.InstaCashCreditLine
          )
          .catch(() => false),
        !this.props.companyStore.hasCompanySetting
          ? this.props.companyApi
              .getCompanySetting()
              .then(setting => this.props.companyStore.setCompanySetting(setting))
              .catch(error => {
                console.warn('Failed to fetch company settings:', error);
              })
          : Promise.resolve(),
      ]);

      await this.fetchLoanRanges(widget, isCreditLineEnabled);

      // b.ii.2.3 — KYC gate. On the Eligible state, when Release_RC_NetbankKyc
      // is on, GET KYC decides whether the borrower is routed into the KYC
      // flow: web shows the intro sub-state then the modal, mobile goes straight
      // to the full-page form (AC #5). Partner-agnostic (isKycFormRequired keys
      // off status, not loanPartnerId).
      let kycRequired = false;
      let kycGateError = false;
      if (widget.state === WidgetStateEnum.Eligible) {
        const netbankKycEnabled = await this.widgetService
          .fetchFeatureFlag(this.props.featureFlagApi, FeatureFlagEnum.NetbankKyc)
          .catch(() => false);
        if (netbankKycEnabled) {
          try {
            await this.props.borrowerStore.setBorrowerStoreValue();
            const borrowerId = this.props.borrowerStore.borrower?.id;
            const kyc = await this.kycApi.getKycStatus(borrowerId);
            kycRequired = isKycFormRequired(kyc.statusId);
          } catch (error) {
            // TC-005 (AC #6): the gate could not be evaluated. Show the KYC
            // system-error surface rather than failing the whole widget, and
            // withhold the MLA selector — falling through to it would let a
            // required KYC be skipped.
            console.error('Failed to evaluate the KYC gate:', error);
            kycGateError = true;
          }
        }
      }

      this.setState({
        widget,
        kycRequired,
        kycGateError,
        isUnderCreditLine:
          widget.state === WidgetStateEnum.OutstandingLoan &&
          'isUnderCreditLine' in widget
            ? (widget as OutstandingLoanWidgetDto).isUnderCreditLine
            : undefined,
        isBelowMinimumMLA:
          widget.state === WidgetStateEnum.OutstandingLoan &&
          'isBelowMinimumMLA' in widget
            ? (widget as OutstandingLoanWidgetDto).isBelowMinimumMLA
            : undefined,
        isCreditLine: isCreditLineEnabled,
      });
    } catch (ex) {
      const errorCode = ex.response?.status;

      const widgetState =
        errorCode === 401 ? WidgetStateEnum.Unauthorized : WidgetStateEnum.Error;

      this.setState({
        widget: { state: widgetState },
        errorStatus: errorCode?.toString() || '',
      });

      if (errorCode === 401) {
        throw new Error('Unauthorized access - stopping initialization.');
      }
    }
  };

  // New method for centralized loan ranges fetching
  fetchLoanRanges = async (widget: WidgetDto, isCreditLineEnabled: boolean = false) => {
    const isEligible = widget.state === WidgetStateEnum.Eligible;

    const isOutstandingWithActiveCreditLine =
      widget.state === WidgetStateEnum.OutstandingLoan &&
      'isUnderCreditLine' in widget &&
      (widget as OutstandingLoanWidgetDto).isUnderCreditLine &&
      isCreditLineEnabled &&
      !(widget as OutstandingLoanWidgetDto).isBelowMinimumMLA;

    // Only fetch if needed and not already fetched
    if (
      (isEligible || isOutstandingWithActiveCreditLine) &&
      !this.props.loanStore.loanRangesFetched
    ) {
      try {
        const loanRanges = await this.props.loansApi.getLoanRanges();
        this.props.loanStore.setLoanRanges(loanRanges);
        this.props.loanStore.setLoanRangesError(null, null);
      } catch (error) {
        const apiError = error as { response?: { status?: number; data?: string } };
        this.props.loanStore.setLoanRangesError(
          apiError.response?.status || 0,
          apiError.response?.data || ''
        );
        this.props.loanStore.setLoanRangesFetched(true);
      }
    }
  };

  async componentDidMount() {
    // The form runs in the dashboard's modal iframe, so completion arrives as a
    // cross-frame signal rather than a callback (see utils/kycCompletion).
    this.stopListeningForKyc = onKycCompleted(() => {
      this.initialize();
    });

    this.loadingStore.show();
    try {
      await this.initialize();
      await this.initializeBorrower();
      this.loadingStore.hide();
    } catch (error) {
      console.error(error);
      this.loadingStore.hide();
    }
  }

  componentWillUnmount() {
    if (this.stopListeningForKyc) this.stopListeningForKyc();
  }

  handleOnFinished = () => {
    this.loadingStore.hide();
    event({
      category: convertWidgetStateToGACategory(this.state.widget.state),
      action: 'View',
    });
  };

  handleOnGetLoan = () => {
    Parent.openParentModal('loan-amount-selection');
  };

  handleOnRetry = async () => {
    this.loadingStore.show();
    await this.initialize();
    if (this.loadingStore.showing) this.loadingStore.hide();
  };

  handleOnError = (error?: any) => {
    let errorStatus = '';
    if (error && error.response && error.response.status) {
      errorStatus = error.response.status;
    }
    this.setState(
      {
        widget: { state: WidgetStateEnum.Error },
        errorStatus,
      },
      () => {
        this.loadingStore.hide();
        event({
          category: convertWidgetStateToGACategory(this.state.widget.state),
          action: 'View',
        });
      }
    );
  };

  handleViewLoanHistory = () => {
    Parent.openParentModal('loan-history');
    //this.props.history.push('loan-history');
  };

  // b.ii.2.4 — the intro's "Continue" hands off to the HR dashboard, which
  // opens the KYC page in its own modal. Drawing the form here would trap it
  // inside the widget iframe (a fixed overlay cannot escape its frame).
  handleStartKyc = () => {
    Parent.openParentModal(KYC_ROUTE);
  };

  get progress() {
    return (
      <div className={styles.progress}>
        <CircleProgress width={72} height={72} />
      </div>
    );
  }

  // b.ii.2 — the KYC flow rendered inside the Eligible state (per OQ #1, no new
  // widget state): the intro, whose Continue asks the HR dashboard to open the
  // KYC page in its modal. The intro is also the re-entry point if the borrower
  // cancels out of that form.
  //
  // TODO(b.ii.2.4 / epic OQ #4): the WebFlow shows the intro for any borrower who
  // still owes KYC — including one with an outstanding loan or a below-minimum
  // MLA. This only reaches the Eligible state, which is the narrower reading.
  // Widen it here if PM confirms the WebFlow scope.
  renderKycFlow() {
    return (
      <KycIntroWidget
        onContinue={this.handleStartKyc}
        onFinished={this.handleOnFinished}
        widgetStore={this.props.widgetStore}
      />
    );
  }

  get widget() {
    switch (this.state.widget.state) {
      case WidgetStateEnum.Eligible:
        // The gate could not be evaluated: show the KYC system-error surface and
        // withhold the MLA selector (b.ii.2.3 AC #6).
        if (this.state.kycGateError) {
          return (
            <KycGateErrorWidget
              onFinished={this.handleOnFinished}
              onError={this.handleOnError}
              onRetry={this.handleOnRetry}
              widgetStore={this.props.widgetStore}
            />
          );
        }
        if (this.state.kycRequired) {
          return this.renderKycFlow();
        }
        return (
          <EligibleWidget
            onFinished={this.handleOnFinished}
            loansApi={this.props.loansApi}
            loanStore={this.props.loanStore}
            onGetLoan={this.handleOnGetLoan}
            onError={this.handleOnError}
            widgetStore={this.props.widgetStore}
          />
        );
      case WidgetStateEnum.Error:
        return (
          <UnauthorizedWidget
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            widgetStore={this.props.widgetStore}
            errorCode={this.state.errorStatus}
          />
        );
      case WidgetStateEnum.BorrowerNotEligible:
        const nonEligibleWidgetDto = this.state.widget as NonEligibleWidgetDto;
        return (
          <BorrowerNotEligibleWidget
            reasons={nonEligibleWidgetDto.nonEligibilityReasons}
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            widgetStore={this.props.widgetStore}
            url={nonEligibleWidgetDto.url}
          />
        );
      case WidgetStateEnum.BenefitsAdmin:
      case WidgetStateEnum.BenefitsEmployee:
      case WidgetStateEnum.CompanyNotEligible:
        const dto = this.state.widget as BenefitsWidgetDto;
        return (
          <CompanyNotEligibleWidget
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            widgetStore={this.props.widgetStore}
            url={dto.url}
            text={dto.text}
            buttonText={dto.buttonText}
            base64Header={dto.base64Header}
            base64Content={dto.base64Content}
            widgetState={this.state.widget.state}
          />
        );
      case WidgetStateEnum.SystemUnderMaintenance:
        return (
          <SystemMaintenanceWidget
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            onRetry={this.handleOnRetry}
            widgetStore={this.props.widgetStore}
          />
        );
      case WidgetStateEnum.OutstandingLoan:
        return (
          <OutstandingLoanWidget
            borrowerApi={this.props.borrowerApi}
            loans={(this.state.widget as OutstandingLoanWidgetDto).outstandingLoans}
            loansApi={this.props.loansApi}
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            onViewLoanHistory={this.handleViewLoanHistory}
            widgetStore={this.props.widgetStore}
            loanStore={this.props.loanStore}
            onGetLoan={this.handleOnGetLoan}
            borrowerStore={this.props.borrowerStore}
            isUnderCreditLine={this.state.isUnderCreditLine}
            isCreditLineEnabled={this.state.isCreditLine}
            isBelowMinimumMLA={this.state.isBelowMinimumMLA}
          />
        );
      case WidgetStateEnum.LoanRequested:
        return (
          <LoanRequestedWidget
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            onViewLoanHistory={this.handleViewLoanHistory}
            widgetStore={this.props.widgetStore}
          />
        );
      case WidgetStateEnum.Unconsented:
        return (
          <UnconsentedWidget
            widgetStore={this.props.widgetStore}
            companyStore={this.props.companyStore}
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            onClick={() => {
              this.setState({
                widget: {
                  state: WidgetStateEnum.ConsentAgreement,
                },
              });
            }}
          />
        );
      case WidgetStateEnum.ConsentAgreement:
        return (
          <ConsentAgreement
            widgetStore={this.props.widgetStore}
            borrowerApi={this.props.borrowerApi}
            borrowerStore={this.props.borrowerStore}
            documentsApi={this.props.documentsApi}
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            onDisagree={() => {
              this.setState({
                widget: {
                  state: WidgetStateEnum.Unconsented,
                },
              });
              this.loadingStore.show();
            }}
            onAgree={agreed => {
              if (agreed) {
                this.loadingStore.show();
              } else this.initialize();
            }}
          />
        );
      case WidgetStateEnum.Unauthorized:
        return (
          <UnauthorizedWidget
            onFinished={this.handleOnFinished}
            onError={this.handleOnError}
            widgetStore={this.props.widgetStore}
            errorCode={this.state.errorStatus}
          />
        );
      default:
        return 'Error';
    }
  }

  render() {
    return (
      <div className={styles.widget}>
        <WidgetHeader
          widgetStore={this.props.widgetStore}
          onViewLoanHistory={this.handleViewLoanHistory}
          widgetState={this.state.widget.state}
        />
        {this.loadingStore.showing && this.progress}
        <Frame className={styles.content} scrollable={true}>
          {this.widget}
        </Frame>
      </div>
    );
  }
}

export default Widget;
